import React from "react"

import { BetaTag } from "@/components/ui/beta-tag"
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible"
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useAuth } from "@/contexts/AuthContext"
import { resolveRouteKind } from "@/lib/routeCostCalculation"
import { COVER_BETA_NOTE } from "@/lib/shiftCover"
import type { CoveredRoute, Location, PlannedRoute, ShiftException } from "@/types/collections"

import PlanningDateGroupHeader from "./PlanningDateGroupHeader"
import PlanningRouteExpandedRow from "./PlanningRouteExpandedRow"
import PlanningRouteRow from "./PlanningRouteRow"
import SortableTableHead from "./SortableTableHead"
import type { NotCheckedReason } from "./usePlanningShiftCover"
import type { RouteMetrics } from "./useRouteMetrics"

interface PlanningDateGroupProps {
  dateKey: string
  /** The day's routes, already in list order. */
  dateRoutes: PlannedRoute[]
  isExpanded: boolean
  onToggle: () => void
  expandedRouteRows: Set<string>
  onToggleRoute: (routeId: string) => void
  onEditRoute: (route: PlannedRoute, e: React.MouseEvent) => void
  sortColumn: string | null
  sortDirection: "asc" | "desc"
  onSort: (column: string) => void
  metricsFor: (route: PlannedRoute) => RouteMetrics
  getLocationById: (locationId: string) => Location | undefined
  loadFactorLoading: boolean
  coverLoading: boolean
  coverFor: (route: PlannedRoute) => ShiftException[] | undefined
  coveredFor: (route: PlannedRoute) => CoveredRoute[] | undefined
  notCheckedReason: (route: PlannedRoute) => NotCheckedReason | null
}

const PlanningDateGroup: React.FC<PlanningDateGroupProps> = ({
  dateKey,
  dateRoutes,
  isExpanded,
  onToggle,
  expandedRouteRows,
  onToggleRoute,
  onEditRoute,
  sortColumn,
  sortDirection,
  onSort,
  metricsFor,
  getLocationById,
  loadFactorLoading,
  coverLoading,
  coverFor,
  coveredFor,
  notCheckedReason,
}) => {
  const sort = { sortColumn, sortDirection, onSort }
  // Same column tests as PlanningRouteRow, so headings and cells line up.
  const { can } = useAuth()

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className="bg-white rounded-lg border border-palette-jungleGreen200 overflow-hidden">
        <PlanningDateGroupHeader
          dateKey={dateKey}
          dateRoutes={dateRoutes}
          isExpanded={isExpanded}
          metricsFor={metricsFor}
        />

        <CollapsibleContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-palette-jungleGreen000/50">
                <TableHead className="w-8"></TableHead>
                <SortableTableHead column="code" className="w-24" {...sort}>
                  Route Code
                </SortableTableHead>
                <SortableTableHead column="status" className="w-32" {...sort}>
                  Status
                </SortableTableHead>
                <SortableTableHead column="timeWindow" className="w-36" {...sort}>
                  Time Window
                </SortableTableHead>
                <SortableTableHead column="startDepot" {...sort}>
                  Start Depot
                </SortableTableHead>
                <SortableTableHead column="endDepot" {...sort}>
                  End Depot
                </SortableTableHead>
                <SortableTableHead column="stops" align="center" {...sort}>
                  Stops
                </SortableTableHead>
                <SortableTableHead column="volume" align="right" {...sort}>
                  Volume
                </SortableTableHead>
                {can.costs && (
                  <SortableTableHead column="loadFactor" align="right" {...sort}>
                    Load factor
                  </SortableTableHead>
                )}
                {can.shiftCover && (
                  <SortableTableHead column="shiftCover" {...sort}>
                    Shift cover
                    <BetaTag title={COVER_BETA_NOTE} />
                  </SortableTableHead>
                )}
                {can.costs && (
                  <>
                    <SortableTableHead column="emptyPct" align="right" {...sort}>
                      Empty %
                    </SortableTableHead>
                    <SortableTableHead column="cost" align="right" {...sort}>
                      Est. Cost
                    </SortableTableHead>
                  </>
                )}
                <SortableTableHead column="recurring" align="center" {...sort}>
                  Recurring
                </SortableTableHead>
                {can.editRoutes && (
                  <TableHead className="text-xs text-center w-20">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {dateRoutes.map((route, routeIndex) => {
                const isRouteExpanded = expandedRouteRows.has(route.id)
                const rowRouteKind = resolveRouteKind(route.route_kind)
                // A border on the first standby route rather than a separator row, so there is no column count to track.
                const startsStandbyBlock =
                  rowRouteKind === "standby" &&
                  (routeIndex === 0 ||
                    resolveRouteKind(dateRoutes[routeIndex - 1].route_kind) !== "standby")
                return (
                  <React.Fragment key={route.id}>
                    <PlanningRouteRow
                      route={route}
                      routeKind={rowRouteKind}
                      startsStandbyBlock={startsStandbyBlock}
                      isExpanded={isRouteExpanded}
                      onToggle={() => onToggleRoute(route.id)}
                      onEdit={(e) => onEditRoute(route, e)}
                      metrics={metricsFor(route)}
                      getLocationById={getLocationById}
                      loadFactorLoading={loadFactorLoading}
                      coverLoading={coverLoading}
                      cover={coverFor(route)}
                      covered={coveredFor(route)}
                      notCheckedReason={notCheckedReason(route)}
                    />
                    {isRouteExpanded && (
                      <PlanningRouteExpandedRow
                        route={route}
                        metrics={metricsFor(route)}
                        onEdit={(e) => onEditRoute(route, e)}
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export default PlanningDateGroup
